import React, { useState } from 'react';
import { Flex, Text } from 'rebass';
import { useHistory, useLocation, Link as RouterLink } from 'react-router-dom';

import Loading from 'app/loading';
import Button from 'app/buttons';
import SignUp from 'babys-first-year/SignUp';

import { usePollShopifyOrder } from './hooks';
import PurchaseSummary from './purchase-summary';

// Where this page sits in the post-purchase flow:
//
//   Shopify checkout
//     -> lands on `/?orderId=<shopify order id>`
//     -> AuthMember (src/auth/member.js) reads the orderId, PATCHes
//        `/v3/shopify_orders/:id/auth_token` to obtain the ANONYMOUS member holding
//        the purchase, stores that auth token, reads `customer-email` / `quantity`
//        off the order, then redirects here with
//        `?customerEmail=&orderQuantity=&orderId=&shopifyOrderId=`
//     -> this page confirms the purchased units exist, then renders SignUp so the
//        customer creates an account or logs in. Because that request carries the
//        anonymous member's token, the server merges the purchase into the
//        resulting account. SignUp's `onDone` then sends them to /account.
//
// This page deliberately does NOT collect an email or call `/auth_token` itself —
// AuthMember already owns that step, and repeating it would re-claim an order that
// has already been claimed.

// AuthMember forwards both ids: `shopifyOrderId` is the Shopify order id (the value
// that arrived on `/?orderId=`), while `orderId` is the order's internal JSON:API
// resource id. Prefer `orderId` (what AuthMember passes as the canonical id) and
// fall back to `shopifyOrderId` so landing here directly still works.
const useOnboardingParams = () => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  return {
    orderId: params.get('orderId') || params.get('shopifyOrderId'),
    customerEmail: params.get('customerEmail') || '',
    firstName: params.get('firstName') || '',
    lastname: params.get('lastName') || ''
  };
};

const Centered = ({ children }) => (
  <Flex
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    minHeight="60vh"
    px={3}
    py={5}
  >
    {children}
  </Flex>
);

const SignInLink = () => (
  <Text
    as={RouterLink}
    to="/"
    mt={3}
    sx={{ color: '#0E0E0E', fontWeight: 500, textDecoration: 'underline' }}
  >
    Go to sign in
  </Text>
);

// A 200 on the order does not mean the purchased units exist yet — a background
// worker provisions them, so `entitlements-count` can still be 0 (and the endpoint
// can return 410) for a short while after checkout.
const PollingGate = ({ orderId, onRetry, children }) => {
  const poll = usePollShopifyOrder(orderId);

  if (poll.status === 'polling') {
    return (
      <Centered>
        <Loading local />
        <Text fontFamily="Poppins" fontSize="16px" mt={3} textAlign="center">
          Setting up your purchase...
        </Text>
      </Centered>
    );
  }

  if (poll.status === 'timed_out') {
    return (
      <Centered>
        <Text fontFamily="Poppins" fontSize="16px" textAlign="center">
          We&rsquo;re still processing your purchase. This can take a few minutes — please check
          back shortly.
        </Text>
        <Button type="button" pill onClick={onRetry} style={{ marginTop: 16 }}>
          Try again
        </Button>
      </Centered>
    );
  }

  if (poll.status === 'claimed') {
    return (
      <Centered>
        <Text fontFamily="Poppins" fontSize="16px" textAlign="center">
          This purchase already belongs to an account.
        </Text>
        <SignInLink />
      </Centered>
    );
  }

  return children();
};

const ShopifyOnboardingPage = () => {
  const { orderId, customerEmail, firstName, lastname } = useOnboardingParams();
  const { push } = useHistory();
  const [pollAttempt, setPollAttempt] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  if (!orderId) {
    return (
      <Centered>
        <Text color="red" fontFamily="Poppins" fontSize="14px" textAlign="center">
          No order was specified.
        </Text>
        <SignInLink />
      </Centered>
    );
  }

  return (
    // Remounting on retry restarts the poll without extra plumbing in the hook.
    <PollingGate
      key={pollAttempt}
      orderId={orderId}
      onRetry={() => setPollAttempt(attempt => attempt + 1)}
    >
      {() =>
        isAuthenticated ? (
          <PurchaseSummary onContinue={() => push('/account')} />
        ) : (
          <SignUp
            isLogin={false}
            hideSkip
            // Prefilled from the Shopify order but still editable — Apple Pay and
            // similar can hand back an address the customer needs to correct.
            initialEmail={customerEmail}
            initialEmailFirstName={firstName}
            initialEmailLastName={lastname}
            onDone={() => {
              setIsAuthenticated(true);
              push('/account');
            }}
          />
        )
      }
    </PollingGate>
  );
};

export default ShopifyOnboardingPage;
